import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { getConfig } from '../../../config'
import { refreshAccessToken, decodeJwtExp, AdminRefreshResponse } from './auth'
import { notify, simplifyBackendError } from '../../shared/utilities/util'

// Small inline type guard for backend-rotated session-reuse detection.
const isSessionReused = (err: unknown): boolean => {
  const msg = (err as AxiosError<{ message?: string }>)?.response?.data?.message
  return msg === 'SESSION_REUSED'
}

// Types for better error handling
interface ApiError {
  message: string
  statusCode: number
  type: 'NETWORK' | 'AUTHENTICATION' | 'VALIDATION' | 'SERVER' | 'UNKNOWN'
  originalError?: any
}

interface InterceptorConfig {
  showSuccessNotifications?: boolean
  showErrorNotifications?: boolean
  excludeFromGlobalErrorHandling?: boolean
  excludeFromGlobalSuccessHandling?: boolean
}

// Extend the axios config type to include our custom properties
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: { startTime: number }
    interceptorConfig?: InterceptorConfig
    // Tags the silent on-load refresh request so the 401 path doesn't tear
    // down a session that doesn't exist. See bootstrapSession().
    _bootstrapRefresh?: boolean
  }
  interface AxiosRequestConfig {
    _bootstrapRefresh?: boolean
  }
}

// Global loading state management
let activeRequests = 0
const loadingCallbacks: Array<(isLoading: boolean) => void> = []

// Subscribe to global loading state changes. Returns an unsubscribe function.
export const subscribeToLoadingState = (callback: (isLoading: boolean) => void) => {
  loadingCallbacks.push(callback)
  return () => {
    const index = loadingCallbacks.indexOf(callback)
    if (index > -1) {
      loadingCallbacks.splice(index, 1)
    }
  }
}

const updateLoadingState = () => {
  const isLoading = activeRequests > 0
  loadingCallbacks.forEach((callback) => callback(isLoading))
}

// Maps an AxiosError to a structured ApiError with a user-facing message and type
const categorizeError = (error: AxiosError): ApiError => {
  const status = error.response?.status
  const message = (error.response?.data as any)?.message || error.message

  if (!error.response) {
    return {
      message: 'Network error. Please check your internet connection.',
      statusCode: 0,
      type: 'NETWORK',
      originalError: error,
    }
  }

  switch (status) {
    case 401:
      return {
        message: 'Authentication failed. Please log in again.',
        statusCode: 401,
        type: 'AUTHENTICATION',
        originalError: error,
      }
    case 403:
      return {
        message: 'You do not have permission to perform this action.',
        statusCode: 403,
        type: 'AUTHENTICATION',
        originalError: error,
      }
    case 400:
    case 422:
      return {
        message: simplifyBackendError(message) || 'Invalid request data.',
        statusCode: status,
        type: 'VALIDATION',
        originalError: error,
      }
    case 500:
    case 502:
    case 503:
    default:
      return {
        message: simplifyBackendError(message) || 'An unexpected error occurred.',
        statusCode: status || 0,
        type: 'UNKNOWN',
        originalError: error,
      }
  }
}

// Create axios instance. withCredentials is required so the browser sends the
// httpOnly `refresh_token` cookie on /admin/auth/refresh (path-scoped to
// /api/v1/admin) and the non-httpOnly `csrf` cookie round-trips alongside it.
export const AxiosInstance = axios.create({
  baseURL: getConfig()?.apiUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// --- In-memory access token --------------------------------------------------
// The access token lives in a module-scoped variable for the lifetime of the
// tab. It is NEVER persisted to localStorage post-Wave 3C. The httpOnly
// refresh cookie is what survives a reload, and the silent bootstrap below
// exchanges it for a fresh access token before the app renders authed UI.
let accessTokenInMemory: string | null = null

export const setAccessToken = (token: string | null) => {
  accessTokenInMemory = token
}

// Read the current access token. Prefers in-memory; falls back to a one-shot
// migration from legacy localStorage['token'] for sessions that existed before
// this wave shipped. After promotion the localStorage entry is wiped so future
// reads go straight to memory and the bootstrap path takes over.
export const getAccessToken = (): string | null => {
  if (accessTokenInMemory) return accessTokenInMemory
  if (typeof window === 'undefined') return null
  const raw = localStorage.getItem('token')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed === 'string') {
      accessTokenInMemory = parsed
      localStorage.removeItem('token')
      return parsed
    }
  } catch {
    // raw was never JSON-encoded — treat it as the bare token.
    if (typeof raw === 'string') {
      accessTokenInMemory = raw
      localStorage.removeItem('token')
      return raw
    }
  }
  return null
}

// Read the non-httpOnly CSRF cookie the backend sets alongside the refresh
// cookie. We echo its value in X-CSRF-Token on cookie-mode refresh calls.
// Only reachable in same-origin setups (e.g., localhost). Cross-domain admins
// can't read the cookie via document.cookie and rely on the in-memory store.
const readCsrfCookie = (): string | null => {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|; )csrf=([^;]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

// CSRF token (memory + localStorage mirror). Cross-domain admin SPA can't
// read the JS-readable csrf cookie via document.cookie because it's host-only
// on the API origin. The backend returns the value in the login/refresh
// response body; we mirror it to localStorage so it survives page reload —
// otherwise the bootstrap refresh after a reload has no csrf to send and the
// backend middleware 403s with "Invalid CSRF token".
let csrfTokenInMemory: string | null = null
const CSRF_STORAGE_KEY = 'csrf'

export const setCsrfToken = (token: string | null) => {
  csrfTokenInMemory = token
  if (typeof window === 'undefined') return
  try {
    if (token) {
      localStorage.setItem(CSRF_STORAGE_KEY, token)
    } else {
      localStorage.removeItem(CSRF_STORAGE_KEY)
    }
  } catch (e) {
    console.warn('Failed to persist csrf token:', e)
  }
}

export const getCsrfToken = (): string | null => {
  if (csrfTokenInMemory) return csrfTokenInMemory
  if (typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem(CSRF_STORAGE_KEY)
      if (stored) {
        csrfTokenInMemory = stored
        return stored
      }
    } catch (e) {
      console.warn('Failed to read csrf token from storage:', e)
    }
  }
  return readCsrfCookie()
}

// Shared promise to deduplicate concurrent token refresh requests
let refreshPromise: Promise<string> | null = null

// Set to `true` when `performLogout()` starts. Any in-flight refresh that
// resolves AFTER this flag is set must drop its result on the floor — we do
// NOT want to write `tokenExpireAt`, re-mint an in-memory access token, or
// schedule another proactive refresh for a session the admin has just torn
// down. Reset to `false` from the signin flow via `resetAuthFlags()`.
let loggedOut = false

// Reset auth-related module flags so a fresh signin can mint tokens cleanly
// even if the admin previously logged out in the same tab. Called from the
// loginAdmin thunk after successful authenticate().
export const resetAuthFlags = () => {
  loggedOut = false
}

// Request interceptor for consistent headers and loading state
AxiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Increment active requests counter
    activeRequests++
    updateLoadingState()

    // Add authentication token if available. getAccessToken reads the
    // in-memory token (populated on login / refresh / bootstrap) and
    // transparently migrates any legacy localStorage['token'] into memory.
    if (!config.headers['Authorization']) {
      const token = getAccessToken()
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
    }

    // Add request timestamp for debugging
    config.metadata = { startTime: Date.now() }

    return config
  },
  (error) => {
    activeRequests--
    updateLoadingState()
    return Promise.reject(error)
  }
)

// Response interceptor for global response and error handling
AxiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Decrement active requests counter
    activeRequests--
    updateLoadingState()

    // Check if global success handling is disabled for this request
    const config = response.config as InternalAxiosRequestConfig & {
      interceptorConfig?: InterceptorConfig
    }
    const shouldShowSuccessNotification = config.interceptorConfig?.showSuccessNotifications

    // Handle success notifications for specific operations
    if (shouldShowSuccessNotification) {
      const successMessage = response.data?.message || response.data?.data?.message
      if (successMessage && typeof successMessage === 'string') {
        notify(successMessage, 'success')
      }
    }

    return response?.data?.data
  },
  async (error) => {
    // Decrement active requests counter
    activeRequests--
    updateLoadingState()

    // Ignore cancelled requests
    if (axios.isCancel(error)) {
      console.warn('Request cancelled:', error.message)
      return Promise.reject(error)
    }

    const axiosError = error as AxiosError
    const originalRequest = axiosError.config as InternalAxiosRequestConfig & {
      _retry?: boolean
      interceptorConfig?: InterceptorConfig
    }
    const categorizedError = categorizeError(axiosError)

    // Log error for debugging
    console.error('API Error:', {
      url: originalRequest?.url,
      method: originalRequest?.method?.toUpperCase(),
      status: axiosError.response?.status,
      type: categorizedError.type,
      message: categorizedError.message,
    })

    // Handle authentication errors (401)
    if (categorizedError.statusCode === 401 && categorizedError.type === 'AUTHENTICATION') {
      return handleAuthenticationError(axiosError, originalRequest)
    }

    // Check if global error handling is disabled for this request
    if (!originalRequest?.interceptorConfig?.excludeFromGlobalErrorHandling) {
      // Show error notification based on error type
      if (originalRequest?.interceptorConfig?.showErrorNotifications !== false) {
        handleErrorNotification(categorizedError)
      }
    }

    // Enhance error object with categorized information
    const enhancedError = {
      ...axiosError,
      apiError: categorizedError,
    }

    return Promise.reject(enhancedError)
  }
)

// Helper function to handle authentication errors and token refresh.
// With Wave 3C the refresh token lives in an httpOnly cookie; the SPA cannot
// inspect it, so we optimistically attempt a refresh on any 401 and let the
// backend tell us whether the cookie is present/valid.
const handleAuthenticationError = async (
  error: AxiosError,
  originalRequest: InternalAxiosRequestConfig & { _retry?: boolean }
): Promise<AxiosResponse> => {
  // Bootstrap-mode refreshes must NEVER call performLogout(): the visitor may
  // be sitting on /login with no session at all. Just bubble the 401 back to
  // the bootstrap caller so it resolves to false and renders the public route.
  if (originalRequest._bootstrapRefresh) {
    return Promise.reject(error)
  }

  // Detect backend-side session reuse (sent by /admin/auth/refresh when the
  // presented refresh token has already been rotated). Dedicated UX + logout.
  if (isSessionReused(error)) {
    notify('For your security, you were signed out because this session was used from another device.', 'warning')
    performLogout()
    return Promise.reject(error)
  }

  // Prevent multiple refresh requests at the same time. Also short-circuit if
  // the 401 came from the refresh endpoint itself.
  if (originalRequest._retry || originalRequest.url?.includes('/admin/auth/refresh')) {
    console.warn('Refresh already in progress or failed, logging out...')
    performLogout()
    return Promise.reject(error)
  }

  originalRequest._retry = true

  try {
    const newAccessToken = await runRefresh()
    if (newAccessToken && originalRequest.headers) {
      originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`
      return AxiosInstance(originalRequest) // Retry the original request
    }
    throw new Error('No access token received')
  } catch (refreshError) {
    console.error('Token refresh failed:', refreshError)
    // performLogout already called from within runRefresh on failure
    return Promise.reject(refreshError)
  }
}

// Shared runner that drives the refresh flow and installs the fresh access
// token. Exposed so the proactive refresh timer can reuse the same deduped
// promise. Uses the cookie flow: the httpOnly refresh cookie is sent
// automatically (withCredentials), and the non-httpOnly csrf cookie is
// echoed back as X-CSRF-Token. If a legacy localStorage refreshtoken is
// present (pre-migration sessions) we pass it through so the backend can
// fall back to body-mode.
//
// `bootstrap: true` means "this call originated from the silent on-load
// attempt". In that mode any error (401, network failure, abort, timeout)
// MUST NOT call performLogout() — a first-time visitor on /login has no
// session to tear down, and bouncing them around post-401 would be wrong.
// The promise rejects so the caller can render the public route.
const runRefresh = (options?: { bootstrap?: boolean }): Promise<string> => {
  if (refreshPromise) return refreshPromise

  const bootstrap = options?.bootstrap === true

  const legacyRefresh = typeof window !== 'undefined' ? localStorage.getItem('refreshtoken') : null
  // Prefer the in-memory csrf token (populated by the last login/refresh response body).
  // Falls back to the cookie reader for the same-origin localhost case where
  // document.cookie can see the value.
  const csrf = getCsrfToken()

  refreshPromise = refreshAccessToken(legacyRefresh, csrf, { bootstrap })
    .then((payload: AdminRefreshResponse | undefined) => {
      // If logout was initiated while this refresh was in flight, drop the
      // result on the floor before touching any auth state. Otherwise we'd
      // resurrect a torn-down session as a zombie (in-memory token + a
      // freshly-armed proactive timer + a tokenExpireAt entry).
      if (loggedOut) {
        throw new Error('logout in progress')
      }

      const newAccessToken: string | undefined = payload?.token
      if (!newAccessToken) {
        throw new Error('Failed to refresh token')
      }

      const exp: number | undefined = payload?.admin?.exp ?? decodeJwtExp(newAccessToken)

      // Access token lives in memory only; refresh token rides the httpOnly
      // cookie from the response (browser sets it automatically).
      setAccessToken(newAccessToken)
      // CSRF token rotates on every refresh; pick up the fresh value from the
      // response body since the cross-domain cookie isn't readable by JS.
      if (payload?.csrf_token) {
        setCsrfToken(payload.csrf_token)
      }

      // Legacy entries may still be present from a pre-migration session —
      // wipe them now that we have a cookie-backed session.
      if (typeof window !== 'undefined') {
        localStorage.removeItem('token')
        localStorage.removeItem('refreshtoken')
      }

      if (exp !== undefined) {
        window.localStorage.setItem('tokenExpireAt', JSON.stringify(exp))
      }

      // Re-arm the proactive timer with the new expiry.
      scheduleProactiveRefresh()
      return newAccessToken
    })
    .catch((refreshError) => {
      // Bootstrap-mode failures (401, network outage, abort, timeout) must
      // stay silent. The caller resolves to false and the router falls
      // through to the public route the visitor asked for.
      if (!bootstrap) {
        console.error('Refresh token request failed:', refreshError)
        performLogout()
      }
      throw refreshError
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

// --- Cross-tab logout sync -------------------------------------------------
// When one admin tab logs out (user-initiated, 401, session-reused, etc.) any
// sibling tabs should also drop to the login screen. Use a BroadcastChannel
// with a name distinct from the main SPA so admin tabs don't clobber customer
// SPA sessions and vice versa.
const ADMIN_AUTH_CHANNEL = 'nhr-admin-auth'
let adminAuthChannel: BroadcastChannel | null = null
let crossTabListenersBound = false

const getAdminAuthChannel = (): BroadcastChannel | null => {
  if (typeof window === 'undefined') return null
  if (typeof BroadcastChannel === 'undefined') return null
  if (adminAuthChannel) return adminAuthChannel
  try {
    adminAuthChannel = new BroadcastChannel(ADMIN_AUTH_CHANNEL)
  } catch (err) {
    console.warn('BroadcastChannel unavailable', err)
    adminAuthChannel = null
  }
  return adminAuthChannel
}

// Perform the local cleanup side of logout without re-broadcasting. Used when
// a peer tab notified us, or the storage event fired because another tab
// cleared the token. Also drops the in-memory access token so any render that
// fires before the navigate() below sees an unauthed state.
const performLocalLogout = () => {
  // Set the same race-guard flag so a refresh in flight in THIS tab (kicked off
  // before the peer-tab broadcast arrived) doesn't restore the access token
  // after we've already torn it down.
  loggedOut = true
  teardownSessionWatcher()
  setAccessToken(null)
  setCsrfToken(null)
  // Defensive: remove legacy entries if any sibling tab hasn't migrated yet.
  localStorage.removeItem('token')
  localStorage.removeItem('refreshtoken')
  localStorage.clear()
  setTimeout(() => {
    window.location.href = '/'
  }, 1000)
}

const bindCrossTabLogoutListeners = () => {
  if (crossTabListenersBound || typeof window === 'undefined') return
  crossTabListenersBound = true

  const channel = getAdminAuthChannel()
  if (channel) {
    channel.onmessage = (event: MessageEvent) => {
      const data = event?.data as { type?: string } | undefined
      if (data?.type === 'logout') {
        performLocalLogout()
      }
    }
  }

  // Fallback for browsers without BroadcastChannel, and a belt-and-braces
  // trigger when another tab clears storage (localStorage.clear fires one
  // `storage` event with key === null). Post-Wave 3C the access token is
  // not in localStorage, so we key on `tokenExpireAt` / `id` (the auth-
  // adjacent entries we still write) plus the legacy `token` key for any
  // unmigrated tabs.
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.storageArea !== localStorage) return
    const authKeyCleared =
      ((event.key === 'token' || event.key === 'tokenExpireAt' || event.key === 'id') && event.newValue === null) ||
      (event.key === null && event.newValue === null)
    if (authKeyCleared && !localStorage.getItem('tokenExpireAt') && !localStorage.getItem('id')) {
      performLocalLogout()
    }
  })
}

// Helper function to perform logout. Broadcasts first so sibling tabs drop out
// of the authed UI before we navigate ourselves. Sets the `loggedOut` module
// flag immediately so any in-flight refresh that resolves after this point
// drops its result on the floor instead of resurrecting the torn-down session.
const performLogout = () => {
  loggedOut = true
  try {
    const channel = getAdminAuthChannel()
    channel?.postMessage({ type: 'logout' })
  } catch (err) {
    console.warn('cross-tab logout broadcast failed', err)
  }
  performLocalLogout()
}

if (typeof window !== 'undefined') {
  bindCrossTabLogoutListeners()
}

// --- Session watcher: proactive refresh ------------------------------------
// Backend is the sole source of truth for session lifetime (60-day rolling
// rotation window, hard-capped at 60 days absolute). The frontend used to
// also enforce a 5h idle timeout here; that has been removed so an admin who
// leaves a tab open overnight is not bounced to /signin while the backend
// session is still valid.

const PROACTIVE_REFRESH_LEAD_MS = 60 * 1000 // refresh 60s before expiry

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null
let listenersBound = false

// Idempotent wakeup check shared by visibilitychange / pageshow / online.
// `setTimeout` is paused while the tab is hidden, the device is asleep, or the
// page is in bfcache — so on resume we may be holding an expired access token
// whose proactive timer never fired. This re-evaluates `tokenExpireAt` against
// wall-clock time and either refreshes immediately or re-arms the timer
// against the (possibly far-shifted) current `Date.now()`.
const checkAndRefreshIfNeeded = () => {
  if (typeof document === 'undefined') return
  const exp = getTokenExpireAtMs()
  if (!exp) return
  // If already expired or within the refresh window, refresh now.
  if (exp - Date.now() <= PROACTIVE_REFRESH_LEAD_MS) {
    runRefresh().catch((err) => console.warn('wakeup-triggered refresh failed', err))
  } else {
    scheduleProactiveRefresh()
  }
}

const onVisibilityChange = () => {
  if (typeof document === 'undefined') return
  if (document.visibilityState !== 'visible') return
  checkAndRefreshIfNeeded()
}

// `pageshow` fires when the page is restored from bfcache (iOS Safari back-
// button, mobile tab restore). `online` fires when the network returns after
// an offline period. Both share the same wakeup logic as visibilitychange.
const onPageShow = () => checkAndRefreshIfNeeded()
const onOnline = () => checkAndRefreshIfNeeded()

const getTokenExpireAtMs = (): number | null => {
  try {
    const raw = localStorage.getItem('tokenExpireAt')
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'number') return null
    // Backend emits exp in seconds (JWT standard). Detect & convert.
    return parsed < 1e12 ? parsed * 1000 : parsed
  } catch {
    return null
  }
}

export const scheduleProactiveRefresh = () => {
  if (typeof window === 'undefined') return

  // Ensure wakeup listeners are wired up once per session.
  if (!listenersBound) {
    document.addEventListener('visibilitychange', onVisibilityChange)
    // `pageshow` covers bfcache restores; `online` covers network-return
    // wakeups. Both feed into the same checkAndRefreshIfNeeded() handler.
    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('online', onOnline)
    listenersBound = true
  }

  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer)
    proactiveRefreshTimer = null
  }

  const expMs = getTokenExpireAtMs()
  if (!expMs) return

  const refreshIn = expMs - Date.now() - PROACTIVE_REFRESH_LEAD_MS
  if (refreshIn <= 0) {
    // Already within/past the refresh window — fire immediately.
    runRefresh().catch((err) => console.warn('immediate proactive refresh failed', err))
    return
  }

  proactiveRefreshTimer = setTimeout(() => {
    runRefresh().catch((err) => console.warn('proactive refresh failed', err))
  }, refreshIn)
}

export const teardownSessionWatcher = () => {
  if (proactiveRefreshTimer) {
    clearTimeout(proactiveRefreshTimer)
    proactiveRefreshTimer = null
  }
  if (listenersBound && typeof window !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('pageshow', onPageShow)
    window.removeEventListener('online', onOnline)
    listenersBound = false
  }
}

// --- Silent bootstrap ------------------------------------------------------
// On page load the in-memory access token is always empty (tabs don't share
// memory). If the user has a valid httpOnly refresh cookie from a previous
// session, we exchange it for a fresh access token BEFORE rendering the
// authed routes.
//
// Always-attempt: we deliberately drop the strict prior-session gate (csrf /
// localStorage presence) because those signals miss real returning users —
// private browsing, manual storage wipe, fresh device with the cookie pinned
// to the apex domain. The cost is one silent 401 per first-time visitor; the
// gain is "cookie alive but localStorage gone" no longer logs anyone out.
//
// Bootstrap mode disables logout-on-failure inside runRefresh — first-time
// visitors landing on /login should NOT be redirected anywhere on a 401 or
// network error. The promise resolves to false and the router renders the
// public route the visitor requested.
//
// IMPORTANT: this MUST NOT run at module-evaluation time. The synchronous prefix
// of an import-time async IIFE would call runRefresh → silentApi.post(...) while
// silentApi (declared further down this file) is still in TDZ / undefined under
// webpack's CJS interop, swallowing the request as a TypeError. Memoize and let
// App.tsx call this from useEffect after React mount when every export const has
// been initialized.
let bootstrapPromise: Promise<boolean> | null = null
export const bootstrapSession = (): Promise<boolean> => {
  if (bootstrapPromise) return bootstrapPromise
  bootstrapPromise = (async () => {
    if (typeof window === 'undefined') return false
    if (accessTokenInMemory) return true
    try {
      await runRefresh({ bootstrap: true })
      return accessTokenInMemory !== null
    } catch {
      return false
    }
  })()
  return bootstrapPromise
}

// Kick off on module load: if an admin already has a legacy token in storage
// (pre-migration session), arm the proactive refresh timer so the existing
// mechanism keeps working until the next refresh promotes the session fully
// into the cookie flow.
if (typeof window !== 'undefined' && localStorage.getItem('token')) {
  scheduleProactiveRefresh()
}

// Helper function to handle error notifications
const handleErrorNotification = (apiError: ApiError) => {
  // Don't show notifications for certain error types or messages
  const suppressedMessages = ['forbidden resource', 'insufficient permissions', 'unauthorized']

  const shouldSuppress = suppressedMessages.some((msg) => apiError.message.toLowerCase().includes(msg.toLowerCase()))

  if (shouldSuppress) {
    return
  }

  // Show different notification types based on error category
  switch (apiError.type) {
    case 'NETWORK':
      notify(apiError.message, 'error')
      break
    case 'AUTHENTICATION':
      // Don't show notification for auth errors as they're handled by logout
      break
    case 'VALIDATION':
      notify(apiError.message, 'warning')
      break
    case 'SERVER':
      notify(apiError.message, 'error')
      break
    default:
      notify(apiError.message, 'error')
      break
  }
}

// Creates a set of HTTP methods (get/post/put/patch/delete) bound to a given InterceptorConfig.
// Use the pre-built exports below instead of calling this directly in most cases.
export const createRequestWithConfig = (config: InterceptorConfig) => {
  return {
    get: (url: string, requestConfig?: any) => AxiosInstance.get(url, { ...requestConfig, interceptorConfig: config }),
    post: (url: string, data?: any, requestConfig?: any) =>
      AxiosInstance.post(url, data, {
        ...requestConfig,
        interceptorConfig: config,
      }),
    put: (url: string, data?: any, requestConfig?: any) =>
      AxiosInstance.put(url, data, {
        ...requestConfig,
        interceptorConfig: config,
      }),
    patch: (url: string, data?: any, requestConfig?: any) =>
      AxiosInstance.patch(url, data, {
        ...requestConfig,
        interceptorConfig: config,
      }),
    delete: (url: string, requestConfig?: any) =>
      AxiosInstance.delete(url, {
        ...requestConfig,
        interceptorConfig: config,
      }),
  }
}

// No notifications, resolves to response.data.data
export const silentApi = createRequestWithConfig({
  showSuccessNotifications: false,
  showErrorNotifications: false,
  excludeFromGlobalErrorHandling: true,
  excludeFromGlobalSuccessHandling: true,
})

// Shows both success and error toasts
export const successNotificationApi = createRequestWithConfig({
  showSuccessNotifications: true,
  showErrorNotifications: true,
})

// Shows error toasts only
export const errorNotificationApi = createRequestWithConfig({
  showSuccessNotifications: false,
  showErrorNotifications: true,
})

// Shows both success and error toasts
export const fullNotificationApi = createRequestWithConfig({
  showSuccessNotifications: true,
  showErrorNotifications: true,
})

// --- Public (unauthenticated) instance ---------------------------------------
// The four flavours above all ride `AxiosInstance`, whose interceptors are
// wrong for a logged-OUT visitor in two ways:
//
//   1. the request interceptor attaches `Authorization: Bearer <token>` from
//      whatever session happens to be in memory, and
//   2. any 401 is funnelled into handleAuthenticationError(), which attempts a
//      refresh and then calls performLogout() — that wipes localStorage and
//      hard-redirects to '/'.
//
// An expired invite / reset token legitimately answers 401, so using a
// flavoured instance for those calls would bounce the visitor off the page
// they just opened from their email, with no explanation. This instance has
// NO interceptors at all: no bearer, no refresh, no logout redirect, no
// toasts (the public pages render their own inline states).
//
// No `withCredentials`: none of the four routes it serves (GET /admin/invite/
// :token, POST /admin/accept-invite, POST /admin/forgot-password, POST
// /admin/reset-password) reads or sets a cookie. accept-invite in particular
// answers `{ message }` only — it does NOT complete a session, and the
// auto-login branch that once assumed it did is gone. Nothing else uses this
// instance (see src/store/api/baseQuery.ts -> publicBaseQuery).
export const PublicAxiosInstance = axios.create({
  baseURL: getConfig()?.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Mirrors what AxiosInstance's response interceptor does (`response.data.data`)
// so callers see the same unwrapped body the rest of the app expects. Falls
// back to `response.data` if a route ever answers un-enveloped.
export const unwrapEnvelope = <T>(response: AxiosResponse<{ data?: T } | undefined>): T => {
  const body = response?.data
  return ((body && typeof body === 'object' && 'data' in body ? body.data : body) ?? body) as T
}

// Export types for use in other files
export type { ApiError, InterceptorConfig }
